/**
 * Helper: Current Car cookie helpers
 * ------------------------------------------------------------------------------
 * Set and get current car model, year, engine code, colour.
 *
 * @namespace currentCarCookie
 */
import Cookies from 'js-cookie'

import {carImage, imageParameters} from './utils'

export default () => {
    /**
   * Retrieve filtered collections/all link.
   * @param {Object} currentCar - currentCar object.
   * @param {String} type - Type of URL to return, `collection`, `filter`, `search`, or `tag`.
   * @returns {String} collections/all with Boost filters URL.
   */
    function collectionUrl (currentCar, type) {
    /**
     * Variables for strings.
     */
        let boostString = ''
        let tagValue = ''
        let tagName = ''

        /**
     * Variables for currentCar.
     */
        let collectionAllUrl = ''
        let filterUrl = ''
        let searchFilter = ''
        let tagFilter = ''

        /**
     * Needs to be outside condition so it can be used in all years.
     */
        const modelFormatted = currentCar.model.replace(/ /g, '-')

        /**
     * Use vehicle code strings if method is lookup.
     */
        if (currentCar.method === 'lookup') {
            boostString = window.theme.strings.standard.vehicleCode
            tagName = 'VehicleCode'
            tagValue = currentCar.vehicleCode

            /**
     * Otherwise use model year tag.
     */
        } else {
            const presentYearString = ` - ${window.theme.strings.currentCar.presentYear}`
            const yearFormatted = `_${currentCar.year.replace(presentYearString, '').replace(/ /g, '')}`

            boostString = window.theme.strings.standard.model
            tagName = 'Model'
            tagValue = modelFormatted + yearFormatted
        }

        /**
     * Format the string used for Boost filter name.
     */
        boostString = boostString.toLowerCase().replace(/ /g, '_')

        /**
     * Create universal search tag.
     * - Either `Filter_VehicleCode: All_VehicleCode` for lookups.
     * - Or `Filter_Model: All_Model` for model dropdown.
     */
        const universalTag = `&pf_t_${boostString}=Filter_${tagName}%3A%20All_${tagName}`

        /**
     * If years has values.
     * - Means all years has been selected.
     */
        if (currentCar.years) {
            const yearArray = currentCar.years.split(',')
            let parameters = ''

            /**
       * Construct all years URL parameter.
       */
            yearArray.forEach((year) => {
                const singleYearFormatted = `_${year.replace(/ /g, '')}`
                parameters += `&pf_t_${boostString}=Filter_${tagName}%3A%20${modelFormatted}${singleYearFormatted}`
            })

            collectionAllUrl = `/collections/all?_=pf${parameters}`
            filterUrl = `?_=pf${parameters}`
            searchFilter = `&_=pf${parameters}`
            tagFilter = `${parameters}`

            /**
     * Else use normal filters.
     */
        } else {
            collectionAllUrl = `/collections/all?_=pf&pf_t_${boostString}=Filter_${tagName}%3A%20${tagValue}`
            filterUrl = `?_=pf&pf_t_${boostString}=Filter_${tagName}%3A%20${tagValue}`
            searchFilter = `&pf_t_${boostString}=${tagValue}`
            tagFilter = `&pf_t_${boostString}=Filter_${tagName}%3A%20${tagValue}`
        }

        if (type === 'filter') {
            return `${filterUrl}${universalTag}`
        } else if (type === 'search') {
            return `${searchFilter}`
        } else if (type === 'tag') {
            return `${tagFilter}${universalTag}`
        }

        return `${collectionAllUrl}${universalTag}`
    }

    /**
   * Retrieve wheel configuration product link.
   * @param {Object} currentCar - currentCar object.
   * @returns {String} wheel configuration product URL.
   */
    function wheelConfigUrl (currentCar) {
        let url = ''

        if (currentCar.method === 'lookup') {
            const vehicleCodeFormatted = currentCar.vehicleCode.toLowerCase()

            url = `/products/wheel-configurator-${vehicleCodeFormatted}`
        } else {
            const modelFormatted = currentCar.model.toLowerCase().replace(/ /g, '-')
            const yearFormatted = currentCar.year.toLowerCase().replace(/ /g, '')

            url = `/products/wheel-configurator-${modelFormatted}-${yearFormatted}`
        }

        return url
    }

    /**
   * Set the current car cookie.
   * @param {Object} currentCar the currentCar object.
   */
    function set (currentCar) {
        const values = {}

        /**
     * Arbitrary order is important for the information to display in correct order.
     */
        values.model = (currentCar.model) ? currentCar.model : false
        values.year = (currentCar.year) ? formatYear(currentCar.year) : false
        values.colour = (currentCar.colour) ? currentCar.colour : false

        values.emissionCode = (currentCar.emissionCode) ? currentCar.emissionCode : false
        values.engineCode = (currentCar.engineCode) ? currentCar.engineCode : false
        values.method = (currentCar.method) ? currentCar.method : false
        values.productionDate = (currentCar.productionDate) ? currentCar.productionDate : false
        values.registration = (currentCar.registration) ? currentCar.registration : false
        values.vehicleCode = (currentCar.vehicleCode) ? currentCar.vehicleCode : false
        values.vinNumber = (currentCar.vinNumber) ? currentCar.vinNumber : false
        values.years = (currentCar.years) ? currentCar.years : false
        values.transmissionCode = (currentCar.transmissionCode) ? currentCar.transmissionCode : false

        Cookies.set('Current_Car', values, {expires: 99})

        if (isCookieSet()) {
            const carDetails = get()
            window.Frame.EventBus.emit('CurrentCar:updated', carDetails)

            /**
             * Unique event for when car is set only.
             */
            window.Frame.EventBus.emit('CurrentCar:unique', carDetails)
        }
    }

    /**
   * Format the year into the correct format ('2000 - 2000' or '2000 - Present')
   * @param {String} year - Year value.
   * @returns {String} the formatted year value.
   */
    function formatYear (year) {
        let formattedYear = year

        if (/[0-9]{4}[-]$/g.test(year)) {
            formattedYear = year.replace('-', ` - ${window.theme.strings.currentCar.presentYear}`)
        } else if (/[0-9-]{9}/g.test(year)) {
            formattedYear = year.replace('-', ' - ')
        }

        return formattedYear
    }

    /**
   * Check to see if the cookie exists.
   * @returns {Event} emit event with current car object.
   */
    function isCookieSet () {
        if (Cookies.get('Current_Car')) {
            return true
        } else {
            return false
        }
    }

    /**
   * Retrieve current car cookie (if it exists).
   * @returns {Object} currentCar object with model, year, engine, and colour.
   */
    function get () {
        let currentCar = Cookies.get('Current_Car')

        if (currentCar) {
            currentCar = JSON.parse(currentCar)

            /**
       * Build image object.
       */
            const images = {
                original: carImage(currentCar),
                100: imageParameters(carImage(currentCar), {size: '100x'}),
                200: imageParameters(carImage(currentCar), {size: '200x'}),
                400: imageParameters(carImage(currentCar), {size: '400x'}),
                750: imageParameters(carImage(currentCar), {size: '750x'}),
                1000: imageParameters(carImage(currentCar), {size: '1000x'}),
                1500: imageParameters(carImage(currentCar), {size: '1500x'})
            }

            currentCar.images = images

            /**
       * Add URL strings.
       */
            currentCar.wheelConfigUrl = wheelConfigUrl(currentCar)

            let type = 'collection'
            currentCar.collectionUrl = collectionUrl(currentCar, type)

            type = 'filter'
            currentCar.collectionFilter = collectionUrl(currentCar, type)

            type = 'search'
            currentCar.searchFilter = collectionUrl(currentCar, type)

            type = 'tag'
            currentCar.tagFilter = collectionUrl(currentCar, type)

            return currentCar
        } else {
            return false
        }
    }

    /**
   * Redirect to a new page.
   * @param {Object} currentCar the currentCar object.
   * @param {String} type action type.
   */
    function redirect (type) {
        const currentCar = get()

        let redirectUrl = ''

        switch (type) {
        case 'wheelConfig':
            redirectUrl = currentCar.wheelConfigUrl
            break

        case 'collection':
            redirectUrl = currentCar.collectionUrl
            break
        }

        window.location.href = redirectUrl
    }

    /**
   * Clear current car.
   */
    function clear () {
    /**
     * Set currentCar so information isn't lost after clear.
     */
        const currentCar = get()

        Cookies.remove('Current_Car')

        if (!isCookieSet()) {
            window.Frame.EventBus.emit('CurrentCar:cleared', currentCar)
        }
    }

    return Object.freeze({
        set,
        isCookieSet,
        get,
        redirect,
        clear
    })
}



// WEBPACK FOOTER //
// ./src/resources/scripts/helpers/current-car-cookie.js